import { useEffect, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { CheckCircle2, XCircle, AlertTriangle, ScanLine } from "lucide-react";
import { api } from "./api";

export function ScanPage() {
  const [result, setResult] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [manual, setManual] = useState("");
  const scannerRef = useRef(null);
  const lockRef = useRef(false);

  const check = async (code) => {
    if (lockRef.current) return;
    lockRef.current = true;
    try {
      const { data } = await api.post("/tickets/scan", { code });
      setResult(data);
    } catch {
      setResult({ result: "invalid", message: "Scan failed" });
    }
    setTimeout(() => { lockRef.current = false; }, 1500);
  };

  const start = async () => {
    setResult(null); setScanning(true);
    const el = document.getElementById("qr-reader");
    if (!el) return;
    const scanner = new Html5Qrcode("qr-reader");
    scannerRef.current = scanner;
    try {
      await scanner.start({ facingMode: "environment" }, { fps: 10, qrbox: 240 },
        (text) => check(text), () => {});
    } catch {
      setScanning(false);
      setResult({ result: "invalid", message: "Camera unavailable — use manual entry" });
    }
  };

  const stop = async () => {
    try { await scannerRef.current?.stop(); scannerRef.current?.clear(); } catch {}
    setScanning(false);
  };

  useEffect(() => () => { stop(); }, []);

  const color = result?.result === "valid" ? "text-emerald-400" : result?.result === "used" ? "text-amber-400" : "text-red-400";
  const Icon = result?.result === "valid" ? CheckCircle2 : result?.result === "used" ? AlertTriangle : XCircle;

  return (
    <div>
      <h1 className="mb-6 font-oswald text-4xl font-700 uppercase tracking-tighter">Scan Tickets</h1>

      <div id="qr-reader" className="mb-4 overflow-hidden border border-white/10" data-testid="qr-reader" />

      {!scanning ? (
        <button data-testid="start-scan" onClick={start} className="flex w-full items-center justify-center gap-2 bg-gold py-4 font-mono text-[12px] uppercase tracking-[0.25em] text-ink-950">
          <ScanLine size={16} /> Start Camera
        </button>
      ) : (
        <button data-testid="stop-scan" onClick={stop} className="w-full border border-white/20 py-4 font-mono text-[12px] uppercase tracking-[0.25em] text-white">Stop</button>
      )}

      {result && (
        <div data-testid="scan-result" className="mt-6 flex flex-col items-center gap-2 border border-white/10 bg-ink-900/60 py-8 text-center">
          <Icon size={40} className={color} />
          <p className={`font-oswald text-2xl uppercase tracking-tight ${color}`}>{result.message}</p>
          {result.event_title && <p className="text-sm text-zinc-400">{result.event_title} · {result.ticket_name}</p>}
        </div>
      )}

      <div className="mt-8 border-t border-white/10 pt-6">
        <p className="mb-3 font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-400">Manual code entry</p>
        <div className="flex gap-2">
          <input data-testid="manual-code" value={manual} onChange={(e) => setManual(e.target.value)} placeholder="Ticket code"
            className="flex-1 border border-white/15 bg-transparent px-4 py-3 text-sm outline-none focus:border-gold" />
          <button data-testid="manual-check" onClick={() => { lockRef.current = false; check(manual.trim()); }} className="bg-gold px-5 font-mono text-[11px] uppercase tracking-[0.2em] text-ink-950">Check</button>
        </div>
      </div>
    </div>
  );
}
